// The bank's jobs (RFC-056 §5), an admin's: what byj does for the bank —
// a kit validated, later a paper drawn and a hand-in graded — waiting,
// running, done or failed with its log, and a failed one tried again. As
// rows (byj-1wv.51.18): the failed first, then those under way, then the
// done; a job opened from its title in a drawer beside the list — what it
// worked on, linked to its page, when it ran and for how long, what it
// spent, its error and its log — read without losing one's place, and a
// failed one tried again from there or from its row's ⋯.
import { Fragment, useCallback, useEffect, useState, type ReactNode } from "react";
import type { BankClient, BankJob } from "../bank";
import { t, whenMinute, word } from "../i18n";
import { Drawer } from "./Drawer";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped } from "./Rows";
import { paperOf, targetName, type BankNames } from "./BankItems";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A job's kind as its row's mark. */
const kindMark: Record<string, string> = { validate: "chk", assemble: "drw", grade: "grd", regrade: "grd", analyse: "ana", trial: "try" };
export const jobKindWord = (kind: string) => word("bank:jobKind", kind) ?? kind;
const underWay = (j: BankJob) => j.state === "queued" || j.state === "running";
const stateChip = (j: BankJob) => `chip${j.state === "failed" ? " chip-warn" : underWay(j) ? " chip-live" : ""}`;

/** Where a job's target is on the page: an item's, or for a hand-in its paper's (the job's input names it); a request has no page. */
function targetPage(j: BankJob): { to: string; label: string } | null {
  const item = /^(itm_[^@/]+)/.exec(j.target);
  if (item) return { to: `/bank/items/${encodeURIComponent(item[1]!)}`, label: t("bank:jobOpenItem") };
  const paper = paperOf(j);
  if (paper) return { to: `/bank/papers/${encodeURIComponent(paper)}`, label: t("bank:jobOpenPaper") };
  return null;
}

/** How long it ran, from its start to its end, in words. */
function took(j: BankJob): string | null {
  if (!j.started_at || !j.finished_at) return null;
  const s = Math.max(0, Math.round((Date.parse(j.finished_at) - Date.parse(j.started_at)) / 1000));
  return s < 60 ? t("bank:jobTookSeconds", { s }) : t("bank:jobTookMinutes", { m: Math.floor(s / 60), s: s % 60 });
}

function JobRow({ j, name, onOpen, onRetry }: { j: BankJob; name: string | null; onOpen: (j: BankJob) => void; onRetry: (j: BankJob) => void }) {
  const menu: MenuItem[] = [
    { label: t("bank:jobDetail"), onSelect: () => onOpen(j) },
    ...(j.state === "failed" ? [{ label: t("bank:jobRetry"), onSelect: () => onRetry(j) }] : []),
  ];
  return (
    <li className={`share-row ${j.state === "done" ? "is-ended" : "is-live"}`} data-testid="bank-job">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {kindMark[j.kind] ?? "job"}
      </span>
      <div className="row-main">
        {/* what it worked on by its name where the bank has one, its id kept small under it; the title opens it */}
        <button className="link row-title" aria-haspopup="dialog" aria-controls="bank-job-detail" onClick={() => onOpen(j)}>
          {jobKindWord(j.kind)} · {name ?? j.target}
        </button>
        <div className="row-sub">
          {[whenMinute(j.created_at), t("bank:jobTries", { n: j.tries }), j.spend_usd > 0 && `$${j.spend_usd.toFixed(3)}`].filter(Boolean).join(" · ")}
          {name && (
            <>
              {" · "}
              <code className="bank-target">{j.target}</code>
            </>
          )}
        </div>
        {j.error && <p className="error small flush">{j.error}</p>}
      </div>
      <div className="row-meta">
        <span className={stateChip(j)}>{word("bank:jobState", j.state) ?? j.state}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: `${jobKindWord(j.kind)} ${j.target}` })} items={menu} />
    </li>
  );
}

/** A job opened: its facts, the page of what it worked on, trying it again, and its log. */
function JobDetail({ j, names, onRetry }: { j: BankJob; names: BankNames; onRetry: (j: BankJob) => void }) {
  const page = targetPage(j);
  // a log reads as its lines were written, a long one scrolled sideways in its box; wrapped on asking, for a narrow screen
  const [wrap, setWrap] = useState(false);
  const facts: [string, ReactNode][] = [
    [t("bank:jobFact.kind"), jobKindWord(j.kind)],
    [
      t("bank:jobFact.target"),
      <>
        {targetName(j.target, names, paperOf(j)) ?? ""} <code className="bank-target">{j.target}</code>
      </>,
    ],
    [t("bank:jobFact.state"), <span className={stateChip(j)}>{word("bank:jobState", j.state) ?? j.state}</span>],
    [t("bank:jobFact.tries"), t("bank:jobTries", { n: j.tries })],
    [t("bank:jobFact.created"), whenMinute(j.created_at)],
    ...(j.started_at ? [[t("bank:jobFact.started"), whenMinute(j.started_at)] as [string, ReactNode]] : []),
    ...(j.finished_at ? [[t("bank:jobFact.finished"), whenMinute(j.finished_at)] as [string, ReactNode]] : []),
    ...(took(j) ? [[t("bank:jobFact.took"), took(j)] as [string, ReactNode]] : []),
    ...(j.spend_usd > 0 ? [[t("bank:jobFact.spend"), `$${j.spend_usd.toFixed(3)}`] as [string, ReactNode]] : []),
  ];
  return (
    <div className="stack" data-testid="bank-job-detail">
      {/* a fact a line, its name beside it: the side panel's and the recording's dl.status */}
      <dl className="status">
        {facts.map(([k, v]) => (
          <Fragment key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </Fragment>
        ))}
      </dl>
      {j.error && <p className="error small flush">{j.error}</p>}
      {(page || j.state === "failed") && (
        <div className="row wrap">
          {j.state === "failed" && (
            <button className="primary small" onClick={() => onRetry(j)}>
              {t("bank:jobRetry")}
            </button>
          )}
          {page && (
            <Link to={page.to} className="button small">
              {page.label}
            </Link>
          )}
        </div>
      )}
      {j.state === "failed" && <p className="muted small flush">{t("bank:jobFailedHow")}</p>}
      <section className="stack tight">
        <div className="row bank-log-head">
          <h3 className="rows-head flush grow">{t("bank:jobLogHead")}</h3>
          {j.log && (
            <button type="button" className="small" aria-pressed={wrap} onClick={() => setWrap(!wrap)}>
              {t("bank:jobLogWrap")}
            </button>
          )}
        </div>
        {j.log ? (
          <pre className={`job-log bank-log${wrap ? " is-wrapped" : ""}`} data-testid="bank-job-log">
            {j.log}
          </pre>
        ) : (
          <p className="muted small flush">{t("bank:jobLogNone")}</p>
        )}
      </section>
    </div>
  );
}

export function BankJobs({ client, names }: { client: BankClient; names: BankNames }) {
  const [jobs, setJobs] = useState<BankJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  // the done are the many and the least looked at: counted, and shown on asking
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(async () => {
    try {
      setJobs(await client.jobs());
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client]);
  useEffect(() => {
    void load();
  }, [load]);
  // a job under way is looked at again until it is done
  useEffect(() => {
    if (!jobs?.some(underWay)) return;
    const timer = setTimeout(() => void load(), 3000);
    return () => clearTimeout(timer);
  }, [jobs, load]);
  const retry = async (j: BankJob) => {
    try {
      await client.retryJob(j.id);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };
  const all = jobs ?? [];
  // the job opened as the list reads it now: a retry or a poll shows in the drawer too
  const open = all.find((j) => j.id === openId) ?? null;
  return (
    <div data-testid="bank-jobs">
      <p className="muted small panel-lead">{t("bank:jobsLead")}</p>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {jobs === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : all.length === 0 ? (
        <EmptyRows text={t("bank:jobsNone")} />
      ) : (
        <Grouped<BankJob>
          id="bank-jobs"
          groups={[
            { title: t("bank:jobsFailed"), key: "failed", className: "rows-head-attention", items: all.filter((j) => j.state === "failed") },
            { title: t("bank:jobsUnderWay"), key: "under-way", className: "rows-head-live", items: all.filter(underWay) },
            (() => {
              const done = all.filter((j) => j.state === "done");
              return {
                title: `${t("bank:jobsDone")} · ${done.length}`,
                uncounted: true,
                key: "done",
                items: showDone ? done : [],
                none:
                  done.length > 0 ? (
                    <button className="small bank-show-more" data-testid="bank-jobs-show-done" onClick={() => setShowDone(true)}>
                      {t("bank:jobsShowDone", { n: done.length })}
                    </button>
                  ) : undefined,
              };
            })(),
          ]}
          row={(j) => <JobRow key={j.id} j={j} name={targetName(j.target, names, paperOf(j))} onOpen={(x) => setOpenId(x.id)} onRetry={(x) => void retry(x)} />}
        />
      )}
      <Drawer
        id="bank-job-detail"
        className="drawer-wide"
        title={open ? `${jobKindWord(open.kind)} · ${targetName(open.target, names, paperOf(open)) ?? open.target}` : ""}
        open={!!open}
        onClose={() => setOpenId(null)}
      >
        {open && <JobDetail j={open} names={names} onRetry={(x) => void retry(x)} />}
      </Drawer>
    </div>
  );
}
